import { useNavigate } from 'react-router-dom';
import { Cpu, ArrowUpRight } from 'lucide-react';

const apps = [
  {
    id: 'forecast-demo',
    title: 'Forecast Engine',
    description: 'K-beauty inventory forecasting with ML-powered demand prediction, confidence intervals, and promo impact analysis.',
    icon: '/images/apps/forecasting_app.png',
    category: 'eCommerce Analytics',
    status: 'POC Demo',
  },
  {
    id: 'propertypro',
    title: 'Property Pro',
    description: 'Real estate intelligence dashboard tracking property development projects through multiple lifecycle phases with interactive data visualizations.',
    icon: '/images/apps/propertypro_thmb.png',
    category: 'Real Estate',
    status: 'POC Demo',
  },
  {
    id: 'tailarena',
    title: 'Tail Arena',
    description: 'A pet-focused platform bringing together community, care, and commerce for pet owners.',
    icon: '/images/apps/tail_arena_logo.png',
    status: 'Live',
    category: 'Pets & Community',
    externalUrl: 'https://www.tailarena.com',
  },
  {
    id: 'bizwiz',
    title: 'Biz Wiz',
    description: 'AI-powered assistant for small business operations — details coming soon.',
    icon: '/images/wabizwiz_logo.png',
    category: 'Business Automation',
    status: 'Coming Soon',
    comingSoon: true,
  },
];

const ROUTES_MAP = {
  'inviteapp':     '/Playground/Inviteapp',
  'forecasting':   '/apps/forecasting',
  'forecast-demo': '/apps/forecast-demo',
  'propertypro':   '/apps/propertypro',
  'tailarena':     '/apps/tailarena',
};

const AppCard = ({ app, onExplore }) => (
  <div className="bg-white rounded-2xl border border-gray-200 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 flex flex-col overflow-hidden">
    {/* Thumbnail */}
    <div className="w-full h-44 bg-gray-50 border-b border-gray-100 flex items-center justify-center overflow-hidden">
      {app.icon ? (
        <img src={app.icon} alt={app.title} className="w-full h-full object-cover" />
      ) : (
        <Cpu className="w-10 h-10 text-gray-300" />
      )}
    </div>

    {/* Body */}
    <div className="p-5 flex flex-col flex-1 gap-3">
      {/* Badges */}
      <div className="flex flex-wrap gap-1.5">
        {app.category && (
          <span className="text-xs px-2 py-0.5 rounded-full bg-blue-50 border border-blue-100 text-blue-600 font-medium">
            {app.category}
          </span>
        )}
        {app.status && (
          <span className="text-xs px-2 py-0.5 rounded-full bg-amber-50 border border-amber-100 text-amber-600 font-medium">
            {app.status}
          </span>
        )}
      </div>

      <h3 className="text-sm font-semibold text-gray-900 leading-snug">{app.title}</h3>
      <p className="text-sm text-gray-500 leading-relaxed line-clamp-3 flex-1">{app.description}</p>

      {app.comingSoon && !app.externalUrl ? (
        <button
          disabled
          className="mt-auto w-full flex items-center justify-center gap-1.5 py-2 rounded-xl bg-gray-100 text-gray-400 text-sm font-medium cursor-not-allowed"
        >
          Coming Soon
        </button>
      ) : (
        <button
          onClick={onExplore}
          className="mt-auto w-full flex items-center justify-center gap-1.5 py-2 rounded-xl bg-gray-900 hover:bg-gray-700 text-white text-sm font-medium transition-colors"
        >
          {app.externalUrl ? 'Visit Site' : 'View Demo'}
          <ArrowUpRight className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  </div>
);

const GameLibrary = () => {
  const navigate = useNavigate();

  const handleExplore = (app) => {
    if (app.externalUrl) {
      window.open(app.externalUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    navigate(ROUTES_MAP[app.id] ?? `/apps/${app.id}`);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">

      {/* Content */}
      <main className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-6">
        {apps.length > 0 ? (
          <div className="flex flex-wrap justify-start gap-5">
            {apps.map((app) => (
              <div key={app.id} className="w-full sm:w-[calc(50%-10px)] lg:w-[calc(33.333%-14px)]">
                <AppCard app={app} onExplore={() => handleExplore(app)} />
              </div>
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <div className="w-14 h-14 rounded-2xl bg-gray-100 flex items-center justify-center mb-4">
              <Cpu className="w-7 h-7 text-gray-400" />
            </div>
            <p className="text-base font-semibold text-gray-900 mb-1">No apps found</p>
          </div>
        )}
      </main>
    </div>
  );
};

export default GameLibrary;
